// Polimorfik ek belge yöneticisi.
// Kullanım: <AttachmentManager entityType="stock" entityId={stk.id} />
// Desteklenen tipler: stock, account, personnel, sales_invoice, purchase_invoice,
// sales_waybill, purchase_waybill, sales_order, purchase_order
import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import { Button, EmptyState } from './ui';

const IconPlus     = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const IconDownload = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>);
const IconTrash    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>);
const IconEye      = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>);

// Dosya uzantısına göre ikon + renk
const fileBadge = (filename) => {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return { icon: '🖼', color: '#0ea5e9', label: ext.toUpperCase() };
  if (ext === 'pdf') return { icon: '📕', color: '#dc2626', label: 'PDF' };
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return { icon: '📄', color: '#2563eb', label: ext.toUpperCase() };
  if (['xls', 'xlsx', 'xlsm', 'csv', 'ods'].includes(ext)) return { icon: '📊', color: '#16a34a', label: ext.toUpperCase() };
  if (['ppt', 'pptx', 'odp'].includes(ext)) return { icon: '📊', color: '#ea580c', label: ext.toUpperCase() };
  if (['zip', 'rar', '7z'].includes(ext)) return { icon: '🗜', color: '#7c3aed', label: ext.toUpperCase() };
  return { icon: '📎', color: '#64748b', label: ext.toUpperCase() || 'FILE' };
};

const isImage = (filename) => /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(filename || '');

const fmtSize = (n) => {
  if (!n) return '—';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / (1024 * 1024)).toFixed(2) + ' MB';
};
const fmtDate = (s) => s ? new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export default function AttachmentManager({ entityType, entityId, readOnly = false, compact = false }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null); // { id, filename }
  const [imageUrls, setImageUrls] = useState({}); // { [id]: blobUrl } — resim önizlemeleri için (auth header'lı fetch)
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!entityType || !entityId) { setList([]); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.get('/attachments', { params: { entity_type: entityType, entity_id: entityId } });
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.error || 'Listelenemedi');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  // Resim olan ekleri auth header'lı fetch et + blob URL üret.
  // <img src> Authorization header gönderemediğinden bu yöntem zorunlu.
  useEffect(() => {
    const images = list.filter(r => isImage(r.filename));
    const newUrls = {};
    let cancelled = false;
    (async () => {
      for (const row of images) {
        if (cancelled) return;
        if (imageUrls[row.id]) { newUrls[row.id] = imageUrls[row.id]; continue; }
        try {
          const r = await api.get('/attachments/' + row.id + '/preview', { responseType: 'blob' });
          if (cancelled) return;
          newUrls[row.id] = URL.createObjectURL(r.data);
        } catch (_) {}
      }
      if (!cancelled) {
        // Eskileri (yeni listede olmayan) revoke et
        Object.entries(imageUrls).forEach(([id, url]) => {
          if (!newUrls[id]) URL.revokeObjectURL(url);
        });
        setImageUrls(newUrls);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [list]);

  // Component unmount → tüm blob URL'leri revoke et
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line
  }, []);

  const upload = async (files) => {
    if (!files || !files.length) return;
    if (!entityId) { setError('Önce kartı kaydedin, sonra dosya ekleyebilirsiniz'); return; }
    setUploading(true); setError('');
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('entity_type', entityType);
        fd.append('entity_id', entityId);
        fd.append('file', file);
        await api.post('/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Yükleme başarısız');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const download = async (row) => {
    try {
      const r = await api.get('/attachments/' + row.id + '/download', { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = row.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      alert(e.response?.data?.error || 'İndirme başarısız');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`"${row.filename}" silinsin mi?`)) return;
    try {
      await api.delete('/attachments/' + row.id);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || 'Silme başarısız');
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    if (readOnly || !entityId) return;
    upload(e.dataTransfer.files);
  };

  const showHint = !entityId;

  return (
    <div>
      {!readOnly && (
        <div
          onDragEnter={(e) => { e.preventDefault(); if (entityId) setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); if (entityId) setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          style={{
            border: `2px dashed ${dragActive ? 'var(--accent-primary, #2563eb)' : 'var(--border-color, #cbd5e1)'}`,
            background: dragActive ? 'rgba(37,99,235,0.06)' : (showHint ? 'var(--bg-secondary, #f8fafc)' : 'transparent'),
            borderRadius: 8,
            padding: compact ? '12px 14px' : '20px 16px',
            textAlign: 'center',
            transition: 'all 0.15s',
            opacity: showHint ? 0.7 : 1,
            marginBottom: 12,
          }}
        >
          {showHint ? (
            <div style={{ color: 'var(--text-tertiary, #888)', fontSize: 13 }}>
              📎 Dosya eklemek için <strong>önce kartı kaydedin</strong>
            </div>
          ) : (
            <>
              <div style={{ fontSize: compact ? 13 : 14, color: 'var(--text-secondary, #64748b)', marginBottom: 8 }}>
                Dosya sürükle-bırak veya seç (resim, PDF, Word, Excel, ZIP — max 25 MB)
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => upload(e.target.files)}
                style={{ display: 'none' }}
                accept="image/*,.pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.xlsm,.csv,.ods,.ppt,.pptx,.odp,.txt,.zip,.rar,.7z"
              />
              <Button size="sm" icon={<IconPlus />} loading={uploading} onClick={() => fileInputRef.current?.click()}>
                {uploading ? 'Yükleniyor...' : 'Dosya Ekle'}
              </Button>
            </>
          )}
        </div>
      )}

      {error && <div style={{ padding: 10, background: 'rgba(220,38,38,0.08)', color: '#991b1b', borderRadius: 4, fontSize: 13, marginBottom: 8 }}>⚠ {error}</div>}

      {loading && <div style={{ padding: 12, color: 'var(--text-tertiary)', fontSize: 13 }}>Yükleniyor...</div>}

      {!loading && list.length === 0 && !showHint && (
        <EmptyState title="Henüz belge yok" description="Yukarıdaki alandan dosya sürükleyip bırakın veya 'Dosya Ekle' butonuna tıklayın." compact />
      )}

      {list.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map(row => {
            const badge = fileBadge(row.filename);
            const img = isImage(row.filename);
            return (
              <div key={row.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: 10,
                background: 'var(--bg-primary, #fff)',
                border: '1px solid var(--border-color, #e5e5e5)',
                borderRadius: 6,
                flexWrap: 'wrap',
              }}>
                {img ? (
                  imageUrls[row.id] ? (
                    <img
                      src={imageUrls[row.id]}
                      alt={row.filename}
                      onClick={() => setPreview(row)}
                      style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: '1px solid var(--border-color, #ddd)' }}
                    />
                  ) : (
                    <div style={{
                      width: 40, height: 40, borderRadius: 4,
                      background: 'var(--bg-secondary, #f1f5f9)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, color: 'var(--text-tertiary)', flexShrink: 0,
                    }}>...</div>
                  )
                ) : (
                  <div style={{
                    width: 40, height: 40, borderRadius: 4,
                    background: badge.color + '22', color: badge.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 700,
                    flexShrink: 0,
                  }}>{badge.icon}</div>
                )}
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary, #222)', wordBreak: 'break-all' }}>{row.filename}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary, #888)' }}>
                    <span style={{ color: badge.color, fontWeight: 600 }}>{badge.label}</span> · {fmtSize(row.file_size)} · {fmtDate(row.created_at)}
                    {row.created_by_name && <> · {row.created_by_name}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {img && <Button variant="ghost" size="sm" iconOnly icon={<IconEye />} title="Önizle" onClick={() => setPreview(row)} />}
                  <Button variant="ghost" size="sm" iconOnly icon={<IconDownload />} title="İndir" onClick={() => download(row)} />
                  {!readOnly && <Button variant="ghost" size="sm" iconOnly icon={<IconTrash />} title="Sil" onClick={() => remove(row)} />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox — resim önizleme */}
      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, cursor: 'zoom-out',
          }}
        >
          <img
            src={imageUrls[preview.id] || ''}
            alt={preview.filename}
            style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', boxShadow: '0 0 40px rgba(0,0,0,0.4)' }}
          />
          <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 13 }}>
            {preview.filename}
          </div>
        </div>
      )}
    </div>
  );
}
